import { FormEvent, useEffect, useMemo, useRef, useState } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { Elements, PaymentElement, useElements, useStripe } from '@stripe/react-stripe-js'
import { loadStripe, type StripeElementsOptions, type Stripe } from '@stripe/stripe-js'
import { AlertCircle, CheckCircle2, CreditCard, ExternalLink, Loader2, ReceiptText } from 'lucide-react'
import { useParams, useSearchParams } from 'react-router-dom'
import { toast } from 'sonner'

import { Button } from '@/components/ui/button'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Skeleton } from '@/components/ui/skeleton'
import { financeApi } from '@/services/finance'
import { getErrorMessage } from '@/lib/api'
import { formatCurrency, formatDate } from '@/lib/utils'

const toNumber = (value?: number | string | null) => {
  if (value === null || value === undefined || value === '') return 0
  const parsed = typeof value === 'number' ? value : Number(value)
  return Number.isFinite(parsed) ? parsed : 0
}

const toRecord = (value: unknown): Record<string, unknown> => (
  value && typeof value === 'object' ? (value as Record<string, unknown>) : {}
)

const readString = (source: Record<string, unknown>, keys: string[]) => {
  for (const key of keys) {
    const value = source[key]
    if (typeof value === 'string' && value.trim()) return value.trim()
    if (typeof value === 'number') return String(value)
  }
  return ''
}

const normalizePublicImageUrl = (value: unknown) => {
  const text = typeof value === 'string' ? value.trim() : ''
  if (!text) return ''
  if (text.startsWith('http://') || text.startsWith('https://')) {
    return text
  }
  if (text.startsWith('/')) {
    return `${window.location.origin}${text}`
  }
  return ''
}

type StripeIntentState = {
  publishableKey: string
  clientSecret: string
  paymentReferenceId: string
  walletId?: string | number | null
}

type StripeInvoicePaymentFormProps = {
  paymentReferenceId: string
  onPaymentSucceeded: (paymentReferenceId: string) => Promise<void>
}

const stripePromiseCache = new Map<string, Promise<Stripe | null>>()

const getStripePromise = (publishableKey: string) => {
  const cached = stripePromiseCache.get(publishableKey)
  if (cached) {
    return cached
  }
  const promise = loadStripe(publishableKey)
  stripePromiseCache.set(publishableKey, promise)
  return promise
}

function StripeInvoicePaymentForm({ paymentReferenceId, onPaymentSucceeded }: StripeInvoicePaymentFormProps) {
  const stripe = useStripe()
  const elements = useElements()
  const [isSubmitting, setIsSubmitting] = useState(false)

  const handleSubmit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault()
    if (!stripe || !elements) {
      return
    }

    setIsSubmitting(true)
    try {
      const result = await stripe.confirmPayment({
        elements,
        confirmParams: {
          return_url: window.location.href,
        },
        redirect: 'if_required',
      })

      if (result.error) {
        const errorWithIntent = result.error as typeof result.error & {
          payment_intent?: { id?: string; status?: string }
        }
        const intentFromError = errorWithIntent.payment_intent
        if (result.error.code === 'payment_intent_unexpected_state' && intentFromError?.status === 'succeeded') {
          await onPaymentSucceeded(intentFromError.id || paymentReferenceId)
          return
        }
        toast.error(result.error.message || 'Payment was not completed.')
        return
      }

      const confirmedIntent = result.paymentIntent
      if (confirmedIntent?.status !== 'succeeded') {
        toast.info(`Payment status: ${confirmedIntent?.status || 'pending'}`)
        return
      }

      await onPaymentSucceeded(confirmedIntent.id || paymentReferenceId)
    } catch (error) {
      toast.error(getErrorMessage(error))
    } finally {
      setIsSubmitting(false)
    }
  }

  return (
    <form className="space-y-4" onSubmit={handleSubmit}>
      <PaymentElement />
      <Button className="w-full" size="lg" type="submit" disabled={!stripe || !elements || isSubmitting}>
        {isSubmitting ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <CreditCard className="mr-2 h-4 w-4" />}
        Pay now
      </Button>
    </form>
  )
}

export default function PublicInvoicePaymentPage() {
  const { jobId, invoiceId, paymentToken } = useParams<{ jobId: string; invoiceId: string; paymentToken: string }>()
  const [searchParams] = useSearchParams()
  const queryClient = useQueryClient()
  const [stripeIntent, setStripeIntent] = useState<StripeIntentState | null>(null)
  const [webhookPendingReference, setWebhookPendingReference] = useState<string | null>(null)
  const handledReturnPaymentIntent = useRef<string | null>(null)
  const paymentStatus = searchParams.get('payment_status')
  const reference = searchParams.get('reference')
  const legacyToken = searchParams.get('token')
  const token = useMemo(() => paymentToken ? decodeURIComponent(paymentToken) : legacyToken, [legacyToken, paymentToken])
  const returnedPaymentIntent = searchParams.get('payment_intent')

  const invoiceQuery = useQuery({
    queryKey: ['public-invoice-payment', jobId, invoiceId, token, Boolean(paymentToken)],
    queryFn: async () => {
      if (token) {
        return financeApi.getPublicInvoicePaymentTokenDetail(token)
      }

      const invoice = await financeApi.getPublicInvoicePaymentDetail(jobId!, invoiceId!, legacyToken)
      return {
        invoice,
        payment_reference: invoice.payment_reference,
        can_pay: invoice.can_pay,
        paid: invoice.paid_status === 'paid',
      }
    },
    enabled: Boolean((paymentToken && token) || (jobId && invoiceId)),
    retry: false,
  })

  const invoice = invoiceQuery.data?.invoice
  const jobContext = toRecord(invoiceQuery.data?.job)
  const invoiceContext = toRecord(invoice)
  const serviceInstanceId = useMemo(() => {
    const fromQuery = searchParams.get('org_instance_id') || ''
    if (fromQuery.trim()) return fromQuery.trim()
    return readString(jobContext, ['service_instance_id', 'serviceInstanceId'])
      || readString(invoiceContext, ['service_instance_id', 'serviceInstanceId'])
  }, [invoiceContext, jobContext, searchParams])

  const serviceInstanceBrandingQuery = useQuery({
    queryKey: ['public-service-instance-branding', serviceInstanceId],
    queryFn: () => financeApi.getPublicServiceInstanceBranding(serviceInstanceId),
    enabled: Boolean(serviceInstanceId),
    retry: false,
  })

  const serviceInstanceBranding = toRecord(serviceInstanceBrandingQuery.data)
  const organizationLogo = useMemo(() => {
    const fromPayment = normalizePublicImageUrl(readString(jobContext, [
      'organization_logo_url',
      'organization_logo',
      'organisation_logo',
      'company_logo',
    ]))
    if (fromPayment) return fromPayment
    return normalizePublicImageUrl(readString(serviceInstanceBranding, [
      'welcome_page_logo',
      'home_page_logo',
      'login_page_logo',
      'side_panel_logo',
      'organization_logo',
      'organisation_logo',
      'company_logo',
    ]))
  }, [jobContext, serviceInstanceBranding])

  const branchLogo = useMemo(() => {
    const fromPayment = normalizePublicImageUrl(readString(jobContext, [
      'branch_logo_url',
      'branch_logo',
      'service_instance_logo',
      'instance_logo',
    ]))
    if (fromPayment) return fromPayment
    return normalizePublicImageUrl(readString(serviceInstanceBranding, [
      'logo',
      'branch_logo',
      'service_instance_logo',
      'instance_logo',
    ]))
  }, [jobContext, serviceInstanceBranding])

  const organizationName = useMemo(
    () => readString(serviceInstanceBranding, ['parent_instance_name', 'organization_name', 'organisation_name']) || 'Organization',
    [serviceInstanceBranding],
  )

  const branchName = useMemo(
    () => readString(serviceInstanceBranding, ['name', 'instance_name']) || 'Branch',
    [serviceInstanceBranding],
  )

  const displayLogo = useMemo(() => {
    if (organizationLogo) return organizationLogo
    if (branchLogo) return branchLogo
    return ''
  }, [organizationLogo, branchLogo])

  const initiatePaymentMutation = useMutation({
    mutationFn: () => paymentToken && token
      ? financeApi.initiateInvoiceGatewayPaymentByToken(token)
      : financeApi.initiateInvoiceGatewayPayment(jobId!, invoiceId!, legacyToken),
    onSuccess: async (data) => {
      await queryClient.invalidateQueries({ queryKey: ['public-invoice-payment', jobId, invoiceId, token, Boolean(paymentToken)] })
      if (data.payment_link) {
        window.location.assign(data.payment_link)
        return
      }
      if (data.signature && data.publishable_key) {
        setStripeIntent({
          publishableKey: data.publishable_key,
          clientSecret: data.signature,
          paymentReferenceId: data.payment_reference_id || '',
          walletId: data.wallet?.id,
        })
        return
      }
      toast.info('Payment gateway initiated.')
    },
    onError: (error) => toast.error(getErrorMessage(error)),
  })

  const invoiceTotal = toNumber(invoice?.snapshot_grand_total)
  const advance = toNumber(invoice?.advance_payment)
  const amountPaid = toNumber(invoice?.amount_paid)
  const outstanding = toNumber(invoice?.outstanding ?? invoice?.payment_due ?? invoiceQuery.data?.amount_due)
  const canPay = Boolean(invoiceQuery.data?.can_pay ?? invoice?.can_pay ?? (invoice?.paid_status !== 'paid' && outstanding > 0))
  const confirmedPaymentReference = reference || invoiceQuery.data?.payment_reference || invoice?.payment_reference || webhookPendingReference

  const stripePromise = useMemo(
    () => stripeIntent?.publishableKey ? getStripePromise(stripeIntent.publishableKey) : null,
    [stripeIntent?.publishableKey],
  )

  const stripeElementsOptions = useMemo<StripeElementsOptions | undefined>(
    () => stripeIntent?.clientSecret
      ? {
          clientSecret: stripeIntent.clientSecret,
          appearance: {
            theme: 'stripe',
          },
        }
      : undefined,
    [stripeIntent?.clientSecret],
  )

  const waitForWebhookConfirmation = async (paymentReferenceId: string) => {
    setWebhookPendingReference(paymentReferenceId)
    toast.info('Payment completed. Waiting for gateway confirmation.')

    for (let attempt = 0; attempt < 12; attempt += 1) {
      await new Promise((resolve) => window.setTimeout(resolve, attempt === 0 ? 1500 : 2500))
      const result = await invoiceQuery.refetch()
      const refreshedInvoice = result.data?.invoice
      const refreshedOutstanding = toNumber(refreshedInvoice?.outstanding ?? refreshedInvoice?.payment_due ?? result.data?.amount_due)
      const isConfirmed = result.data?.paid === true
        || refreshedInvoice?.paid_status === 'paid'
        || result.data?.can_pay === false
        || refreshedInvoice?.can_pay === false
        || refreshedOutstanding <= 0
      if (isConfirmed) {
        setStripeIntent(null)
        setWebhookPendingReference(null)
        toast.success(paymentReferenceId ? `Payment confirmed. Reference ${paymentReferenceId}.` : 'Payment confirmed.')
        return
      }
    }

    toast.info('Payment completed. Gateway confirmation is still processing.')
  }

  useEffect(() => {
    if (!token || !returnedPaymentIntent || handledReturnPaymentIntent.current === returnedPaymentIntent) {
      return
    }
    handledReturnPaymentIntent.current = returnedPaymentIntent
    waitForWebhookConfirmation(returnedPaymentIntent).catch((error) => toast.error(getErrorMessage(error)))
  }, [returnedPaymentIntent, token])

  const statusNotice = useMemo(() => {
    if (paymentStatus === 'success') {
      return {
        className: 'border-emerald-200 bg-emerald-50 text-emerald-800',
        icon: <CheckCircle2 className="h-4 w-4" />,
        text: confirmedPaymentReference ? `Payment received. Reference ${confirmedPaymentReference}.` : 'Payment received.',
      }
    }
    if ((invoiceQuery.data?.paid || invoice?.paid_status === 'paid') && confirmedPaymentReference) {
      return {
        className: 'border-emerald-200 bg-emerald-50 text-emerald-800',
        icon: <CheckCircle2 className="h-4 w-4" />,
        text: `Payment confirmed. Reference ${confirmedPaymentReference}.`,
      }
    }
    if (invoiceQuery.data?.paid || invoice?.paid_status === 'paid') {
      return {
        className: 'border-emerald-200 bg-emerald-50 text-emerald-800',
        icon: <CheckCircle2 className="h-4 w-4" />,
        text: invoiceQuery.data?.message || 'Payment is already paid.',
      }
    }
    if (paymentStatus === 'failed') {
      return {
        className: 'border-red-200 bg-red-50 text-red-800',
        icon: <AlertCircle className="h-4 w-4" />,
        text: reference ? `Payment was not completed. Reference ${reference}.` : 'Payment was not completed.',
      }
    }
    return null
  }, [paymentStatus, reference, invoiceQuery.data?.paid, invoiceQuery.data?.message, invoice?.paid_status, confirmedPaymentReference])

  if (invoiceQuery.isLoading) {
    return (
      <main className="min-h-screen bg-muted/30 px-4 py-8">
        <div className="mx-auto max-w-3xl space-y-4">
          <Skeleton className="h-10 w-64" />
          <Skeleton className="h-52 w-full" />
          <Skeleton className="h-36 w-full" />
        </div>
      </main>
    )
  }

  if (invoiceQuery.isError || !invoice) {
    return (
      <main className="min-h-screen bg-muted/30 px-4 py-8">
        <div className="mx-auto max-w-3xl rounded-lg border bg-background p-6">
          <div className="flex items-center gap-2 text-destructive">
            <AlertCircle className="h-5 w-5" />
            <h1 className="text-lg font-semibold">Invoice not available</h1>
          </div>
          <p className="mt-2 text-sm text-muted-foreground">{getErrorMessage(invoiceQuery.error)}</p>
        </div>
      </main>
    )
  }

  return (
    <main className="min-h-screen bg-muted/30 px-4 py-8">
      <div className="mx-auto max-w-3xl space-y-5">
        <div className="flex items-start gap-3">
          <div className="flex h-10 w-10 items-center justify-center rounded-md border bg-background">
            <ReceiptText className="h-5 w-5" />
          </div>
          <div>
            <h1 className="text-2xl font-semibold text-foreground">Invoice Payment</h1>
            <p className="text-sm text-muted-foreground">Invoice {invoice.invoice_number || invoice.invoice_no}</p>
          </div>
        </div>

        {(organizationLogo || branchLogo) ? (
          <div className="rounded-xl border border-border/70 bg-card/60 px-4 py-5">
            <div className="mx-auto flex max-w-xl flex-col items-center gap-2 text-center">
              <img
                src={displayLogo}
                alt={`${organizationName} logo`}
                className="h-14 w-14 rounded object-contain"
              />
              <p className="text-base font-semibold text-foreground">{organizationName}</p>
            </div>
            {branchName ? (
              <p className="mt-3 text-left text-xs text-muted-foreground">{branchName}</p>
            ) : null}
          </div>
        ) : null}

        {statusNotice ? (
          <div className={`flex items-center gap-2 rounded-md border px-3 py-2 text-sm ${statusNotice.className}`}>
            {statusNotice.icon}
            <span>{statusNotice.text}</span>
          </div>
        ) : null}

        {webhookPendingReference ? (
          <div className="flex items-center gap-2 rounded-md border border-amber-200 bg-amber-50 px-3 py-2 text-sm text-amber-800">
            <Loader2 className="h-4 w-4 animate-spin" />
            <span>Payment completed. Waiting for gateway confirmation.</span>
          </div>
        ) : null}

        <Card>
          <CardHeader>
            <CardTitle>Invoice Details</CardTitle>
          </CardHeader>
          <CardContent className="grid gap-4 sm:grid-cols-2">
            <div>
              <p className="text-xs uppercase text-muted-foreground">Job</p>
              <p className="font-medium">{invoice.job_title || `Job ${invoice.job}`}</p>
            </div>
            <div>
              <p className="text-xs uppercase text-muted-foreground">Customer</p>
              <p className="font-medium">{invoice.client_name || '-'}</p>
            </div>
            <div>
              <p className="text-xs uppercase text-muted-foreground">Issued</p>
              <p className="font-medium">{invoice.issued_at ? formatDate(invoice.issued_at) : '-'}</p>
            </div>
            <div>
              <p className="text-xs uppercase text-muted-foreground">Due Date</p>
              <p className="font-medium">{invoice.due_date ? formatDate(invoice.due_date) : '-'}</p>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle>Payment Summary</CardTitle>
          </CardHeader>
          <CardContent className="space-y-3">
            <div className="flex items-center justify-between text-sm">
              <span className="text-muted-foreground">Invoice total</span>
              <span className="font-medium">{formatCurrency(invoiceTotal)}</span>
            </div>
            <div className="flex items-center justify-between text-sm">
              <span className="text-muted-foreground">Advance</span>
              <span className="font-medium">{formatCurrency(advance)}</span>
            </div>
            <div className="flex items-center justify-between text-sm">
              <span className="text-muted-foreground">Paid</span>
              <span className="font-medium">{formatCurrency(amountPaid)}</span>
            </div>
            <div className="flex items-center justify-between border-t pt-3">
              <span className="font-medium">Balance</span>
              <span className="text-xl font-semibold">{formatCurrency(outstanding)}</span>
            </div>
            <Button
              className="mt-3 w-full"
              size="lg"
              disabled={!canPay || initiatePaymentMutation.isPending || Boolean(stripeIntent) || Boolean(webhookPendingReference)}
              onClick={() => initiatePaymentMutation.mutate()}
            >
              {initiatePaymentMutation.isPending ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <CreditCard className="mr-2 h-4 w-4" />}
              {canPay ? 'Make Payment' : 'Invoice Tallied'}
            </Button>
            {invoice.payment_url ? (
              <a className="inline-flex items-center gap-1 text-sm text-primary" href={invoice.payment_url}>
                Payment link <ExternalLink className="h-3.5 w-3.5" />
              </a>
            ) : null}
          </CardContent>
        </Card>

        {stripeIntent && stripePromise && stripeElementsOptions ? (
          <Card>
            <CardHeader>
              <CardTitle>Card Payment</CardTitle>
            </CardHeader>
            <CardContent>
              <Elements key={stripeIntent.clientSecret} stripe={stripePromise} options={stripeElementsOptions}>
                <StripeInvoicePaymentForm
                  paymentReferenceId={stripeIntent.paymentReferenceId}
                  onPaymentSucceeded={waitForWebhookConfirmation}
                />
              </Elements>
            </CardContent>
          </Card>
        ) : null}
      </div>
    </main>
  )
}